import {
  Checkbox,
  NumberInput,
  SingleSelectDropdown,
  Spinner,
  Text,
} from "@carbon6/c6-frontend-design";
import type { ChangeEvent, ReactNode } from "react";
import { hatcheryDropdownModes, hatcheryTextModes } from "@/app/theme";
import { DatasetPreview } from "@/components/dataset";
import { EmptyState, noticeChipClassName } from "@/shared/ui/emptyState/public";
import { inlineSpinnerClassName } from "@/shared/ui/loadingState/public";
import {
  isAnnotationSetSelectable,
  toAnnotationSetChoices,
  type AnnotationSetsSource,
} from "./annotationSetSelectable";
import {
  canSelectTargetDataset,
  isRoleLocked,
  type SubmitDatasetSelection,
} from "./submitWizardForm";
import { openDatasetDetailWindow } from "@/app/datasetDetailWindow";
import styles from "./SelectedDatasetList.module.css";

// 담은 학습 입력 목록. **순서가 곧 target_dataset_index 가 세는 축**이라 카드마다 번호를 표시한다.
// 확정 요약도 `datasets[0]` 형식으로 적으므로, 번호가 없으면 둘을 대조할 수 없다.
// 제목과 담은 수는 구성 스텝 내비가 소유한다.
//
// 표가 아니라 카드다. 표에서는 컬럼 머리글(`dataset`·`role` 등)이 값보다 넓은 자리를 차지하고,
// 담은 항목은 이름 한 줄로만 보인다. 카드는 picker 가 이미 받은 섬네일·요약을 그대로 표시해서
// (추가 요청 0) 담은 항목을 먼저 보이게 한다.
//
// **여기서는 빼지 않는다**(사용자 확정). 담기와 빼기는 ① 실험 설정의 picker 한 곳이 소유한다.
// 두 화면에서 같은 목록을 줄일 수 있으면, 뺀 위치에 따라 picker 의 체크 상태와 이 목록이
// 어긋난다. 이 카드는 담은 항목의 **사용 방식**(role·weight·target)만 정한다.

// 비운 값(null)은 균등 배분이라 옳은 값이다. 규칙을 어긴 값은 0 이하를 입력한 값뿐이다.
function weightInvalid(weight: number | null): boolean {
  return weight !== null && weight <= 0;
}

function hintId(index: number): string {
  return `submit-dataset-weight-hint-${index}`;
}

function isTarget(targetDatasetIndex: number | null, index: number): "true" | "false" {
  return targetDatasetIndex === index ? "true" : "false";
}

// 값은 제출 payload 의 role 을 그대로 표시한다. 화면 어휘와 config·API 어휘를 다르게 두지 않는다.
const ROLE_OPTIONS = [
  { value: "train", label: "train" },
  { value: "val", label: "val" },
];

// 세트를 쓰지 않는 상태도 목록의 한 항목이다. 고른 값을 되돌릴 대상이 있어야 한다.
const NO_ANNOTATION_SET = "";
const NO_ANNOTATION_SET_OPTION = { value: NO_ANNOTATION_SET, label: "쓰지 않음" };

// 라벨 · 컨트롤 · 힌트 세 칸을 그리드에 **직접** 둔다. 래퍼로 감싸면 세 열이 한 칸으로 합쳐진다.
// 라벨은 aria-hidden 이다. 각 컨트롤이 데이터셋 이름을 담은 aria-label 을 가지므로, 라벨을
// 접근 이름으로 또 연결하면 같은 컨트롤을 두 이름으로 읽는다.
function FieldRow({
  label,
  hint,
  hintId: hintElementId,
  hintTone = "muted",
  children,
}: {
  label: string;
  hint?: ReactNode;
  hintId?: string;
  hintTone?: "muted" | "danger";
  children: ReactNode;
}): ReactNode {
  return (
    <>
      <span className={styles.fieldLabel} aria-hidden="true">
        {label}
      </span>
      <div className={styles.fieldControl}>{children}</div>
      <span
        id={hintElementId}
        className={
          hintTone === "danger" ? `${styles.fieldHint} ${styles.fieldHintDanger}` : styles.fieldHint
        }
      >
        {hint}
      </span>
    </>
  );
}

// weight 힌트. 값이 규칙을 어기면 그 사실을, val 이면 쓰이지 않는다는 사실을, 그 외에는 넣을
// 값을 표시한다. 오류 문구만 있으면 옳은 값을 넣는 동안 안내가 없다.
function weightHint(dataset: SubmitDatasetSelection): string {
  if (weightInvalid(dataset.weight)) {
    return "0 보다 커야 합니다.";
  }
  if (dataset.role !== "train") {
    return "val 입력에는 쓰이지 않습니다.";
  }
  return "비우면 균등하게 섞습니다 · 0 보다 큰 수";
}

// 세트 줄의 힌트. 고른 세트가 있으면 그 규모를, 없으면 고를 수 있는 수를 표시한다. 상태 문구
// (확인 중·실패·0건)는 컨트롤 자리가 이미 표시하므로 반복하지 않는다.
function annotationHint(
  dataset: SubmitDatasetSelection,
  annotationSets: AnnotationSetsSource,
): string | null {
  const sets = annotationSets.byDataset.get(dataset.uuid);
  if (sets === undefined || sets.length === 0) {
    return null;
  }
  if (dataset.annotationSetUuid !== null) {
    const chosen = sets.find((set) => set.uuid === dataset.annotationSetUuid);
    const rendered = chosen?.materialize?.rendered_count ?? null;
    return rendered === null ? null : `keyframe ${rendered}개`;
  }
  const selectable = sets.filter(isAnnotationSetSelectable).length;
  return `고를 수 있는 세트 ${selectable}개`;
}

// 후보 상태별 컨트롤. 아직 못 받음 / 실패 / 0건 / 목록의 네 경우를 구별한다.
// 실패·대기는 제출을 막지 않는다. BE 가 마지막 게이트다.
// 이 줄은 카드마다 반복되고 드롭다운과 높이가 같다. 그래서 면을 깔지 않는다. 대기는 글자 옆 스피너,
// 실패는 주황 칩이다(docs/rules.md §19).
function renderAnnotationControl(
  dataset: SubmitDatasetSelection,
  annotationSets: AnnotationSetsSource,
  onSelect: (datasetUuid: string, next: string | null) => void,
  maskGenerationBlocked: string | null,
): ReactNode {
  const sets = annotationSets.byDataset.get(dataset.uuid);
  if (sets === undefined) {
    return (
      <span className={styles.annotationNote}>
        {annotationSets.status === "error" ? (
          <span className={noticeChipClassName}>세트 목록을 불러오지 못했습니다.</span>
        ) : (
          <>
            <Spinner size="sm" className={inlineSpinnerClassName} />
            세트를 확인하는 중입니다.
          </>
        )}
        {dataset.annotationSetUuid !== null && (
          <button
            type="button"
            className={styles.annotationLink}
            onClick={() => onSelect(dataset.uuid, null)}
          >
            쓰지 않음
          </button>
        )}
      </span>
    );
  }
  if (sets.length === 0) {
    return (
      <span className={styles.annotationNote}>
        만들어 둔 세트가 없습니다.{" "}
        <button
          type="button"
          className={styles.annotationLink}
          onClick={() =>
            openDatasetDetailWindow({
              datasetUuid: dataset.uuid,
              displayName: dataset.label,
              initialTab: "annotation",
            })
          }
        >
          만들기
        </button>
      </span>
    );
  }
  // 잠긴 항목도 목록에 남긴다. 설명 줄에 사유를 표시해서, 방금 만든 세트를 왜 아직 쓸 수 없는지 알린다.
  const options = [
    NO_ANNOTATION_SET_OPTION,
    ...toAnnotationSetChoices(sets).map((choice) => ({
      value: choice.uuid,
      label: choice.label,
      description: maskGenerationBlocked ?? choice.detail ?? undefined,
      disabled: maskGenerationBlocked !== null || !choice.selectable,
    })),
  ];
  return (
    <SingleSelectDropdown
      mode={hatcheryDropdownModes.default}
      label={`${dataset.label} Annotation Set`}
      fullWidth
      options={options}
      value={dataset.annotationSetUuid ?? NO_ANNOTATION_SET}
      neutralValue={NO_ANNOTATION_SET}
      onValueChange={(value) => onSelect(dataset.uuid, value === NO_ANNOTATION_SET ? null : value)}
    />
  );
}

export function SelectedDatasetList({
  datasets,
  targetDatasetIndex,
  annotationSets,
  maskGenerationBlocked,
  onChange,
  onTargetChange,
}: {
  datasets: readonly SubmitDatasetSelection[];
  targetDatasetIndex: number | null;
  // 입력별 어노테이션 세트 후보. 세트를 만드는 표면은 데이터셋 상세가 소유한다. 여기서는 고르기만 한다.
  annotationSets: AnnotationSetsSource;
  maskGenerationBlocked: string | null;
  onChange: (datasets: SubmitDatasetSelection[]) => void;
  onTargetChange: (index: number | null) => void;
}): ReactNode {
  if (datasets.length === 0) {
    return (
      <EmptyState size="sm">
        아직 담은 Dataset이 없습니다. 실험 설정 스텝에서 Training에 쓸 Dataset을 골라 주세요.
      </EmptyState>
    );
  }

  // 비율·학습 타깃은 train 이 2개 이상일 때만 뜻이 있다. 그 전에는 노브를 숨겨서
  // BE 의 target_index_needs_two_train 위반이 구조적으로 생기지 않게 한다.
  const showMixControls = canSelectTargetDataset(datasets);
  // 입력이 하나뿐이면 고를 수 있는 역할은 train 뿐이다. 폼이 값을 이미 고정했고, 여기서는 잠근다.
  const roleLocked = isRoleLocked(datasets);

  function patch(index: number, next: Partial<SubmitDatasetSelection>): void {
    onChange(datasets.map((dataset, at) => (at === index ? { ...dataset, ...next } : dataset)));
  }

  // 세트는 **Dataset 단위**로 적용한다. 같은 Dataset 을 두 번 담았으면 두 카드가 같은 값을 가진다.
  // mask 자리가 Dataset 트리에 하나뿐이라 BE 는 서로 다른 세트를 422 로 거부한다. 폼 정규화도
  // 같은 규칙이지만 첫 등장 값을 우선한다. 그래서 방금 고른 값이 되돌려지지 않게 여기서 모든
  // 카드에 적용한다.
  function patchAnnotationSet(datasetUuid: string, next: string | null): void {
    if (next !== null && maskGenerationBlocked !== null) return;
    onChange(
      datasets.map((dataset) =>
        dataset.uuid === datasetUuid ? { ...dataset, annotationSetUuid: next } : dataset,
      ),
    );
  }

  return (
    <div className={styles.list}>
      {datasets.map((dataset, index) => (
        <article key={`${dataset.uuid}:${index}`} className={styles.card}>
          {/* 번호는 제출 payload 의 배열 위치다. 장식이 아니라 타깃 지정이 세는 축이다. 그래서
              카드 맨 위에서 열을 나누고, 아래 구분선·입력이 이 자리를 넘지 않게 한다. */}
          <span className={styles.index} aria-label={`입력 ${index}번`}>
            #{index}
          </span>
          <div className={styles.body}>
            <div className={styles.head}>
              <DatasetPreview
                label={dataset.label}
                imageUrl={dataset.preview?.thumbnailUrl ?? null}
                mode="table"
                className={styles.thumb}
              />
              <div className={styles.headText}>
                <Text variant={hatcheryTextModes["body-bold"]} className={styles.name}>
                  {dataset.label}
                </Text>
                {dataset.preview === undefined || dataset.preview.meta.length === 0 ? null : (
                  <span className={styles.meta}>{dataset.preview.meta.join(" · ")}</span>
                )}
              </div>
            </div>
            {/* 아래 단은 라벨 열 · 컨트롤 열 · 힌트 열의 세 칸이다. 라벨을 컨트롤 위에 쌓으면
                컨트롤 폭이 서로 달라서 필드마다 왼쪽 끝이 어긋나고, 힌트도 매번 다른 자리에서
                시작한다. 열 폭을 고정하면 카드끼리·필드끼리 세 시작선이 모두 하나다. */}
            <div className={styles.fields}>
              <FieldRow
                label="role"
                // 입력이 하나뿐이면 고를 수 있는 값은 train 뿐이다. 잠근 이유를 그 줄에 표시한다.
                hint={roleLocked ? "입력이 하나면 train 뿐입니다." : null}
              >
                <span className={styles.roleBox}>
                  <SingleSelectDropdown
                    mode={hatcheryDropdownModes.default}
                    label={`${dataset.label} role`}
                    options={ROLE_OPTIONS}
                    value={dataset.role}
                    disabled={roleLocked}
                    fullWidth
                    onValueChange={(value) =>
                      patch(index, { role: value === "val" ? "val" : "train" })
                    }
                  />
                </span>
              </FieldRow>
              {showMixControls ? (
                <>
                  <FieldRow
                    label="weight"
                    hintId={hintId(index)}
                    // 넣을 값을 항상 표시하고, 값이 규칙을 어길 때만 같은 자리에서 문구를 바꾼다.
                    // c6 의 validationHint 는 값과 관계없는 상시 danger 힌트라 쓰지 않는다.
                    hintTone={weightInvalid(dataset.weight) ? "danger" : "muted"}
                    hint={weightHint(dataset)}
                  >
                    <span className={styles.weightBox}>
                      <NumberInput
                        fullWidth
                        value={dataset.weight}
                        aria-label={`${dataset.label} weight`}
                        // 계약은 세 계층 모두 `gt=0` 인 float 다. 음수는 막고 소수점은 허용한다.
                        // 문자는 NumberInput 의 패턴 게이트가 이미 거른다.
                        allowNegative={false}
                        // 0 은 패턴을 통과하지만 BE 는 422 를 준다. 이 입력은 `type="text"` 라
                        // `min`/`max` 가 효과가 없다. 그래서 힌트 자리의 문구가 규칙을 어긴 값을 알린다.
                        invalid={weightInvalid(dataset.weight)}
                        // 비율은 train 입력을 섞는 축이다. val 에는 쓰이지 않으므로 잠근다.
                        disabled={dataset.role !== "train"}
                        aria-describedby={hintId(index)}
                        // 지정하지 않으면(비움) training 이 균등 배분으로 정한다.
                        onChange={(value) => patch(index, { weight: value })}
                      />
                    </span>
                  </FieldRow>
                </>
              ) : null}
              <FieldRow
                label="Annotation Set"
                hint={maskGenerationBlocked ?? annotationHint(dataset, annotationSets)}
              >
                {renderAnnotationControl(
                  dataset,
                  annotationSets,
                  patchAnnotationSet,
                  maskGenerationBlocked,
                )}
              </FieldRow>
              {showMixControls ? (
                <FieldRow
                  label="target"
                  hint={
                    dataset.role === "train"
                      ? "1 epoch 이 이 Dataset을 한 번 도는 길이가 됩니다."
                      : "train 입력에만 걸 수 있습니다."
                  }
                >
                  {/* 라디오로 두면 한 번 고른 뒤 되돌릴 수 없다(같은 이름 그룹에 해제가 없다).
                      체크박스는 다시 눌러 해제할 수 있다. 다른 카드를 켜면 폼의 단일 값이 그 카드로 옮겨 간다.
                      켜지면 테두리·배경이 붙어서 컨트롤이 "이 카드가 target" 이라는 표식이 된다. */}
                  <span
                    className={styles.target}
                    data-selected={isTarget(targetDatasetIndex, index)}
                  >
                    <Checkbox
                      checked={targetDatasetIndex === index}
                      // target 은 train 입력에만 지정할 수 있다(BE target_index_not_train).
                      disabled={dataset.role !== "train"}
                      aria-label={`${dataset.label} 을 target 으로`}
                      onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        onTargetChange(event.target.checked ? index : null)
                      }
                    />
                  </span>
                </FieldRow>
              ) : null}
            </div>
          </div>
        </article>
      ))}
    </div>
  );
}
